import MoreOptionsModal from '@/components/ContentMoreOptions/Modal/MoreOptionsModal';
import CourseSection from '@/components/CourseSection';
import EmptySection from '@/components/EmptySection';
import FilterVisibilityModal from '@/components/FilterVisibilityModal';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { CourseRequest } from '@/models/CourseResquest';
import { FilterOptionRequest } from '@/models/FilterOptionRequest';
import { PaginationData } from '@/models/PaginationData';
import { analyticsService, FlowEvents } from '@/services/AnalyticsService';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { closeModal, openModal } from '@/store/common/actions';
import { coursesRequest, myCoursesClear } from '@/store/list/action';
import { ListTypes } from '@/store/list/types';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import './style.scss';

const MyCreationsPage: React.FC = () => {
    const { strings, loadingMyCourses, totalMyCourses, personId, modules, modalIsVisible } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        loadingMyCourses: state.lists.listData.listMyCourseStudentView.loading,
        totalMyCourses: state.lists.listData.listMyCourseStudentView.total,
        personId: state.identity.data.person.id,
        modules: state.appOrganization.data.modules,
        modalIsVisible: state.common.modal?.[Constants.modal.MORE_OPTIONS]?.isVisible,
    }));

    const MY_COURSES_VIEW_TYPE = 'listMyCourseStudentView';

    const dispatch = useDispatch();

    const history = useHistory();

    const [filteredVisibilityOption, setFilteredVisibilityOption] = useState<FilterOptionRequest>();

    useEffect(() => {
        analyticsService.logEvent(FlowEvents.MY_CREATIONS);
        getMyCourses();

        return () => {
            dispatch(myCoursesClear());
        };
    }, []);

    useEffect(() => {
        if (modalIsVisible) {
            dispatch(closeModal(Constants.modal.MORE_OPTIONS));
        }
    }, [modalIsVisible]);

    const handleFilterVisibilityModal = (filteredVisibilityOption?: FilterOptionRequest) => {
        setFilteredVisibilityOption(filteredVisibilityOption);

        dispatch(myCoursesClear());

        getMyCourses();

        dispatch(closeModal(Constants.modal.MORE_OPTIONS_VISIBILITY));
    };

    const clickOptionHandler = (f: Function) => (event: SyntheticEvent) => {
        event.stopPropagation();
        event.preventDefault();
        f(event);
    };

    const changeVisibility: React.MouseEventHandler<HTMLButtonElement> = clickOptionHandler(() => {
        dispatch(
            openModal({
                id: Constants.modal.MORE_OPTIONS_VISIBILITY,
                layoutModal: <FilterVisibilityModal onSelect={handleFilterVisibilityModal} selectedOption={filteredVisibilityOption} />,
                isVisible: true,
                isCloseAllowed: true,
            }),
        );
    });

    const getMyCourses = (): void => {
        dispatch(
            coursesRequest({
                excludeEnrollments: true,
                onlyMine: true,
                ignoreLocale: true,
                personId: personId,
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID]),
                paginationData: {
                    offset: 0,
                    limit: 0,
                    orderBy: 'pc.createdOn desc',
                    courseRequestSuccessType: ListTypes.MY_COURSES_SUCCESS,
                    doRequest: true,
                } as PaginationData,
            } as CourseRequest),
        );
    };

    const coursesLoaded = () => totalMyCourses && totalMyCourses > 0 && !loadingMyCourses;

    const handleCreateListButtonClick = () => {
        history.push('/mine/courses/create');
    };

    const handleCreateOrFilterButtonsRender = (): JSX.Element => (
        <>
            <div className="btn-container">
                <div className={`position ${coursesLoaded() ? 'left' : ''}`}>
                    <button className="btn" onClick={handleCreateListButtonClick} type="button">
                        {strings.CREATE_NEW_LIST}
                    </button>
                    <div className="btn-container__filter">
                        {filteredVisibilityOption && (
                            <button className="btn btn-clear-filter" onClick={() => handleFilterVisibilityModal(undefined)} type="button">
                                {strings.CLEAN_FILTER}
                            </button>
                        )}
                        <button className="btn btn-filter" onClick={changeVisibility} type="button">
                            {strings.FILTER_BY_VISIBILITY} <i className="material-icons-outlined">{Constants.icons.FILTER_ALT_OUTLINED}</i>
                        </button>
                    </div>
                </div>
            </div>
            {filteredVisibilityOption && (
                <div className="btn-container">
                    <div className={`position ${coursesLoaded() ? 'left' : ''}`}>
                        <button className="btn btn-filtered" type="button">
                            {filteredVisibilityOption === FilterOptionRequest.PRIVATE ? strings.PRIVATE : strings.PUBLIC}
                            <i className="material-icons" onClick={() => handleFilterVisibilityModal(undefined)}>
                                {Constants.icons.CLOSE}
                            </i>
                        </button>
                    </div>
                </div>
            )}
        </>
    );

    const emptySectionRender = (): JSX.Element => (
        <div className="empty-section-container">
            <EmptySection iconName={Constants.icons.SMS_FAILED} message={strings.EMPTY_SECTION_MESSAGE} />
        </div>
    );

    const myCoursesSectionRender = (): JSX.Element => (
        <div className="dynamic-groupers__container container">
            <CourseSection
                filteredVisibilityOption={filteredVisibilityOption}
                key={`course_section_${MY_COURSES_VIEW_TYPE}`}
                mode={'view'}
                viewType={MY_COURSES_VIEW_TYPE}
            />
        </div>
    );

    const sectionHandler = (handler: JSX.Element): JSX.Element => (!loadingMyCourses ? handler : <Loader styles={{ marginTop: 80 }} />);

    const pageRender = (): JSX.Element => sectionHandler(coursesLoaded() ? myCoursesSectionRender() : emptySectionRender());

    return (
        <div className="mycreations">
            {handleCreateOrFilterButtonsRender()}
            {pageRender()}
            <MoreOptionsModal modalId={Constants.modal.MORE_OPTIONS_VISIBILITY} />
        </div>
    );
};
export default MyCreationsPage;
